// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ReviewedTransaction } from '../../lib/import/types.js';
import type { Account } from '../../types.js';
import { formatAmount } from '../../lib/eventLog.js';

interface ImportReviewTableProps {
  transactions: ReviewedTransaction[];
  accounts: Account[];
  onChange: (updated: ReviewedTransaction[]) => void;
}

type StatusLabelKey = 'review.status.new' | 'review.status.duplicate' | 'review.status.probableDup';

const STATUS_BADGE: Record<string, { labelKey: StatusLabelKey; color: string }> = {
  new: { labelKey: 'review.status.new', color: '#3D9970' },
  'fitid-duplicate': { labelKey: 'review.status.duplicate', color: '#4A4D65' },
  'exact-duplicate': { labelKey: 'review.status.duplicate', color: '#4A4D65' },
  'probable-duplicate': { labelKey: 'review.status.probableDup', color: '#E67E22' },
};

export function ImportReviewTable({ transactions, accounts, onChange }: ImportReviewTableProps) {
  const { t } = useTranslation(['import', 'common', 'errors']);
  const newCount = transactions.filter((t) => t.status === 'new' && t.selected).length;
  const dupCount = transactions.filter((t) => t.status !== 'new').length;
  const suggestedCount = transactions.filter((t) => t.suggestedByMatcher && t.status === 'new').length;

  const toggleOne = (idx: number) => {
    const updated = [...transactions];
    const tx = updated[idx]!;
    updated[idx] = { ...tx, selected: !tx.selected };
    onChange(updated);
  };

  const selectAll = () => {
    onChange(transactions.map((t) => ({ ...t, selected: t.status === 'new' || t.status === 'probable-duplicate' })));
  };

  const deselectDuplicates = () => {
    onChange(transactions.map((t) => ({
      ...t,
      selected: t.status === 'new',
    })));
  };

  const setTransferAccount = (idx: number, accountId: string) => {
    const updated = [...transactions];
    const tx = updated[idx]!;
    const next: ReviewedTransaction = { ...tx, suggestedByMatcher: false };
    if (accountId) {
      next.transferAccountId = accountId;
    } else {
      delete next.transferAccountId;
    }
    updated[idx] = next;
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-sm">
        <span className="text-positive font-medium font-mono">{t('review.newCount', { count: newCount })}</span>
        <span className="text-text-muted">{t('review.duplicateCount', { count: dupCount })}</span>
        {suggestedCount > 0 && (
          <span className="text-accent text-xs">✦ {t('review.autoCategorizedCount', { count: suggestedCount })}</span>
        )}
        <div className="ml-auto flex gap-2">
          <button
            onClick={selectAll}
            className="text-xs px-2 py-1 border border-border text-text-muted hover:bg-bg-elevated transition-colors"
          >
            {t('review.selectAll')}
          </button>
          <button
            onClick={deselectDuplicates}
            className="text-xs px-2 py-1 border border-border text-text-muted hover:bg-bg-elevated transition-colors"
          >
            {t('review.deselectDuplicates')}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated border-b border-border text-xs text-text-muted uppercase tracking-wide">
            <tr>
              <th className="w-8 px-3 py-2"><input type="checkbox" onChange={() => selectAll()} /></th>
              <th className="px-3 py-2 text-left">{t('review.columns.date')}</th>
              <th className="px-3 py-2 text-left">{t('review.columns.description')}</th>
              <th className="px-3 py-2 text-left">{t('review.columns.category')}</th>
              <th className="px-3 py-2 text-right">{t('review.columns.amount')}</th>
              <th className="px-3 py-2 text-center">{t('review.columns.status')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {transactions.map((tx, idx) => {
              const badgeMeta = STATUS_BADGE[tx.status];
              const badge = badgeMeta
                ? { label: t(badgeMeta.labelKey), color: badgeMeta.color }
                : { label: tx.status, color: '#7B7F9E' };
              const isDup = tx.status !== 'new';
              return (
                <tr
                  key={idx}
                  className={isDup ? 'opacity-40 bg-bg' : 'bg-bg hover:bg-bg-elevated'}
                >
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={tx.selected}
                      onChange={() => toggleOne(idx)}
                      disabled={tx.status === 'fitid-duplicate' || tx.status === 'exact-duplicate'}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-text-muted">{tx.date}</td>
                  <td className="px-3 py-2 text-text max-w-xs truncate" title={tx.description}>
                    {tx.description}
                    {tx.duplicateReason && (
                      <span className="ml-2 text-xs text-text-subtle">{tx.duplicateReason}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {tx.suggestedByMatcher && (
                        <span className="text-accent text-xs flex-shrink-0" title={t('review.autoCategorized')}>✦</span>
                      )}
                      <select
                        value={tx.transferAccountId ?? ''}
                        onChange={(e) => setTransferAccount(idx, e.target.value)}
                        disabled={isDup}
                        className="flex-1 min-w-0 bg-bg border border-border text-text text-xs px-2 py-1 focus:border-accent focus:outline-none disabled:opacity-50"
                        style={{ maxWidth: '180px' }}
                      >
                        <option value="">{t('review.columns.imbalance')}</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono ${tx.amount < 0 ? 'text-negative' : 'text-positive'}`}>
                    {formatAmount(tx.amount, tx.currency)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="text-xs font-medium" style={{ color: badge.color }}>
                      {badge.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
